import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { Descriptions, Empty, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import styled from 'styled-components';

import { getRecommendationWebEvidence } from 'apis/RecommendationsAPI';
import { StyledTable } from 'common/Table/StyledTable';
import ErrorMessage from 'components/lib/ErrorMessage';
import Loader from 'components/lib/Loader';
import Modal from 'components/lib/Modal';
import { RecommendationWebEvidencePageType, RecommendationWebEvidenceRunType } from 'types/recommendations';

import {
  RECOMMENDATION_WEB_EVIDENCE_CLAIMS_TAB_KEY,
  RECOMMENDATION_WEB_EVIDENCE_MODAL_WIDTH,
  RECOMMENDATION_WEB_EVIDENCE_QUERY_KEY,
  WEBSITE_ACCESS_STATUS_LABELS,
} from './constants';
import { getEvidencePageTitle, humanizeEvidenceValue } from './helpers';
import RecommendationEvidenceArtifactLink from './RecommendationEvidenceArtifactLink';
import RecommendationEvidenceClaims from './RecommendationEvidenceClaims';
import RecommendationEvidenceDetailsModal, {
  RecommendationEvidenceDetailsPreviewType,
} from './RecommendationEvidenceDetailsModal';
import RecommendationEvidenceVerificationFailure from './RecommendationEvidenceVerificationFailure';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 0px;
`;

const EvidenceModal = styled(Modal)`
  .ant-modal-body {
    height: 70vh;
    overflow-y: auto;
    scrollbar-gutter: stable;
  }
`;

const EvidenceRunLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PageTypes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const EvidenceTable = StyledTable as React.ComponentType<TableProps<RecommendationWebEvidencePageType>>;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const EvidenceTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 20px;
  }
`;

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const getPageColumns = (
  onOpenDetails: (preview: RecommendationEvidenceDetailsPreviewType) => void
): ColumnsType<RecommendationWebEvidencePageType> => [
  {
    title: 'Page',
    render: (_, page) => <Typography.Text>{getEvidencePageTitle(page.extracted_data)}</Typography.Text>,
  },
  {
    title: 'URL',
    dataIndex: 'source_url',
    render: (sourceUrl: string, page) => (
      <Typography.Link
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-test-id={`aici-website-evidence-source-link-${page.id}`}
      >
        {sourceUrl}
      </Typography.Link>
    ),
  },
  {
    title: 'Type',
    dataIndex: 'page_type',
    render: (pageType: string) => (
      <PageTypes>
        {pageType ? pageType.split(',').map((value) => <Tag key={value}>{humanizeEvidenceValue(value)}</Tag>) : '—'}
      </PageTypes>
    ),
  },
  {
    title: 'Screenshot',
    dataIndex: 'artifact',
    render: (artifact: string | null, page) =>
      artifact ? (
        <RecommendationEvidenceArtifactLink
          artifactUrl={artifact}
          label={`Screenshot of ${getEvidencePageTitle(page.extracted_data)}`}
          dataTestId={`aici-website-evidence-screenshot-${page.id}`}
          thumbnail
        />
      ) : (
        '—'
      ),
  },
  {
    title: 'Details',
    render: (_, page) => (
      <Typography.Link
        href="#"
        data-test-id={`aici-website-evidence-page-details-${page.id}`}
        onClick={(event) => {
          event.preventDefault();
          onOpenDetails({
            title: `${getEvidencePageTitle(page.extracted_data)} details`,
            tabs: [{ key: String(page.id), label: 'Page details', value: page.extracted_data }],
          });
        }}
      >
        Open details
      </Typography.Link>
    ),
  },
];

type EvidenceRunContentProps = {
  evidenceRun: RecommendationWebEvidenceRunType;
  onOpenDetails: (preview: RecommendationEvidenceDetailsPreviewType) => void;
};

const EvidenceRunContent = ({ evidenceRun, onOpenDetails }: EvidenceRunContentProps) => {
  const summary = evidenceRun.summary_data;
  const sourceReason = typeof summary.source_status?.reason === 'string' ? summary.source_status.reason : '';
  let collectionStatus = 'Complete';
  if (summary.access_blocked) {
    collectionStatus =
      WEBSITE_ACCESS_STATUS_LABELS[summary.access_diagnostics?.kind || ''] || 'Blocked by site security';
  } else if (sourceReason && summary.pages_discovered === undefined) {
    collectionStatus = humanizeEvidenceValue(sourceReason);
  } else if (summary.time_limit_reached) {
    collectionStatus = 'Stopped at time limit';
  } else if (summary.candidate_safety_limit_reached) {
    collectionStatus = 'Stopped at page limit';
  }

  return (
    <EvidenceRunLayout>
      <Descriptions bordered size="small" column={3}>
        <Descriptions.Item label="Collected">
          {DATE_FORMATTER.format(new Date(evidenceRun.created_at))}
        </Descriptions.Item>
        <Descriptions.Item label="Expires">{DATE_FORMATTER.format(new Date(evidenceRun.expires_at))}</Descriptions.Item>
        <Descriptions.Item label="Status">{collectionStatus}</Descriptions.Item>
        <Descriptions.Item label="Pages discovered">{summary.pages_discovered ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Pages rendered">{summary.pages_rendered ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Elapsed">
          {summary.elapsed_seconds === undefined ? '—' : `${summary.elapsed_seconds} seconds`}
        </Descriptions.Item>
      </Descriptions>
      {summary.access_blocked && summary.access_diagnostics?.kind && (
        <Typography.Link
          data-test-id={`website-access-diagnostics-${evidenceRun.id}`}
          onClick={() =>
            onOpenDetails({
              title: 'Website access diagnostics',
              tabs: [
                { key: String(evidenceRun.id), label: 'Access diagnostics', value: summary.access_diagnostics ?? {} },
              ],
            })
          }
        >
          Access diagnostics
        </Typography.Link>
      )}
      <FeatureList>
        {Object.entries(summary.features || {}).map(([feature, evidence]) => (
          <Tag key={feature}>
            {humanizeEvidenceValue(feature)}: {humanizeEvidenceValue(evidence.status)}
          </Tag>
        ))}
      </FeatureList>
      {evidenceRun.pages.length > 0 ? (
        <TableContainer>
          <EvidenceTable
            columns={getPageColumns(onOpenDetails)}
            dataSource={evidenceRun.pages}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </TableContainer>
      ) : (
        <Empty description="No pages saved" />
      )}
    </EvidenceRunLayout>
  );
};

type Props = {
  open: boolean;
  recommendationId: number;
  communityId: number;
  communityName: string;
  onClose: () => void;
};

const RecommendationEvidenceModal = ({ open, recommendationId, communityId, communityName, onClose }: Props) => {
  const [detailsPreview, setDetailsPreview] = useState<RecommendationEvidenceDetailsPreviewType | null>(null);
  const { data, isError, isLoading } = useQuery({
    queryKey: [RECOMMENDATION_WEB_EVIDENCE_QUERY_KEY, recommendationId, communityId],
    queryFn: () => getRecommendationWebEvidence(recommendationId, communityId),
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const evidenceRuns = [...(data?.evidence_runs || [])].sort((left, right) => {
    if (left.is_primary !== right.is_primary) {
      return left.is_primary ? -1 : 1;
    }
    return left.community_name.localeCompare(right.community_name);
  });
  const claims = data?.verification_data.claims || [];
  const verificationFailure = data?.verification_data.failure;
  const communityTabItems = evidenceRuns.map((evidenceRun) => ({
    key: String(evidenceRun.community_id),
    label: (
      <span data-test-id={`aici-website-evidence-community-tab-${evidenceRun.community_id}`}>
        {evidenceRun.is_primary ? `${evidenceRun.community_name} (Primary)` : evidenceRun.community_name}
      </span>
    ),
    children: <EvidenceRunContent evidenceRun={evidenceRun} onOpenDetails={setDetailsPreview} />,
  }));
  const tabItems = claims.length
    ? [
        {
          key: RECOMMENDATION_WEB_EVIDENCE_CLAIMS_TAB_KEY,
          label: <span data-test-id="aici-website-evidence-claims-tab">Verified claims ({claims.length})</span>,
          children: (
            <RecommendationEvidenceClaims
              claims={claims}
              evidenceRuns={evidenceRuns}
              onOpenDetails={setDetailsPreview}
            />
          ),
        },
        ...communityTabItems,
      ]
    : communityTabItems;

  const handleClose = () => {
    setDetailsPreview(null);
    onClose();
  };

  return (
    <>
      <EvidenceModal
        open={open}
        classNames={{ body: 'mini-scroll' }}
        title={`${communityName} comparison evidence`}
        footer={null}
        width={RECOMMENDATION_WEB_EVIDENCE_MODAL_WIDTH}
        destroyOnClose
        onCancel={handleClose}
        data-test-id={`aici-website-evidence-modal-${communityId}`}
      >
        <ModalContent>
          <Loader active={isLoading} />
          <ErrorMessage
            active={isError}
            text="Website evidence is unavailable for this recommendation and community."
          />
          {verificationFailure && <RecommendationEvidenceVerificationFailure failure={verificationFailure} />}
          {!isLoading && !isError && tabItems.length === 0 && <Empty description="No website evidence available" />}
          {tabItems.length > 0 && (
            <EvidenceTabs
              defaultActiveKey={claims.length ? RECOMMENDATION_WEB_EVIDENCE_CLAIMS_TAB_KEY : String(communityId)}
              items={tabItems}
              data-test-id="aici-website-evidence-tabs"
            />
          )}
        </ModalContent>
      </EvidenceModal>
      <RecommendationEvidenceDetailsModal
        preview={open ? detailsPreview : null}
        onClose={() => setDetailsPreview(null)}
      />
    </>
  );
};

export default RecommendationEvidenceModal;
